import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { useAuth } from '../lib/auth.jsx';
import ProductDrawer from '../components/ProductDrawer.jsx';
import ProductPicker from '../components/ProductPicker.jsx';
import { authFetch } from '../lib/authFetch.js';
import { useFxDefaults, fxDefaultOf, fxRangeWarn } from '../lib/fxDefaults.js';
import FxDefaultsBar from '../components/FxDefaultsBar.jsx';
import { TZ, thParts, thToday, thDay } from '../lib/thTime.js';   // เวลาไทยเสมอ ไม่ตามนาฬิกาเครื่อง
import { parseFile, fingerprint, guessMapping, isComplete, applyMapping, matchLines, refreshLines, SYSTEM_FIELDS, LINE_CATEGORIES } from '../lib/pi.js';

const CUR_SYM = { THB: '฿', JPY: '¥', USD: '$', EUR: '€', GBP: '£', CNY: '¥' };
const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d });

const ArrowSvg = ({ stroke = '#0A0A0A' }) => (
  <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke={stroke} strokeWidth="1.6"/></svg>
);

// แบรนด์ของ PI จากระบบนำเข้า: ใช้ชื่อซัพในใบก่อน — ไม่มี consignment ค่อยอ่านจาก prefix ของรหัส shipment
// (ตรวจข้อมูลจริง 3 ส.ค.: SP=Snow Peak · TC=Tokyo Crafts · CM=Claymore · DOD · EN=Evernew · NG=NANGA)
const INB_BRAND_PREFIX = { SP: 'Snow Peak', TC: 'TOKYOCRAFTS', CM: 'CLAYMORE', DOD: 'DoD', EN: 'EVERNEW', NG: 'NANGA' };
const INB_BRAND_CANON = [
  ['snowpeak', 'Snow Peak'], ['tokyocraft', 'TOKYOCRAFTS'], ['claymore', 'CLAYMORE'],
  ['dod', 'DoD'], ['evernew', 'EVERNEW'], ['nanga', 'NANGA'],
];
function inbBrandOf(row) {
  const raw = String(row.con?.supplier_name || '').trim();
  if (raw) {
    const k = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
    for (const [pre, name] of INB_BRAND_CANON) if (k.startsWith(pre)) return name;
    return raw;
  }
  const code = String(row.ship?.code || '').trim().toUpperCase();
  const pre = code.split('-')[0];
  return INB_BRAND_PREFIX[pre] ?? (pre || '—');
}

// สถานะ shipment ในแอป Inbound (enum inbound.shipment_status) → ป้ายภาษาไทย + สี (in = กำลังมา, ok = เข้าแล้ว, x = ยกเลิก)
const INB_STATUS = {
  ordered: ['สั่งแล้ว', 'in'], paid: ['จ่ายเงินแล้ว', 'in'], payment_confirmed: ['ยืนยันการจ่าย', 'in'],
  ready_to_ship: ['พร้อมส่ง', 'in'], pickup_arranged: ['นัดรับของ', 'in'], in_transit: ['กำลังขนส่ง', 'in'],
  docs_received: ['ได้เอกสารแล้ว', 'in'], form_verified: ['ตรวจฟอร์มแล้ว', 'in'], customs_submitted: ['ยื่นศุลกากร', 'in'],
  duty_paid: ['จ่ายภาษีแล้ว', 'in'], delivered: ['ส่งถึงแล้ว', 'in'], received: ['รับเข้าแล้ว', 'ok'], void: ['ยกเลิก', 'x'],
};
// ประเภทเอกสาร (enum inbound.req_type) → ชื่อที่อ่านง่าย
const INB_DOC_TYPE = {
  proforma_invoice: 'PI', commercial_invoice: 'Commercial Invoice', packing_list: 'Packing List', bl: 'B/L', awb: 'AWB',
  booking_confirmation: 'ใบยืนยันการจอง', declaration: 'ใบขนสินค้า', duty_receipt: 'ใบเสร็จภาษีนำเข้า',
  payment_proof: 'สลิปโอนเงิน', insurance: 'ประกันภัย', certificate_of_origin: 'ใบรับรองแหล่งกำเนิด',
  product_dossier: 'เอกสารสินค้า', attachment: 'ไฟล์แนบ',
};

// บาร์โค้ดที่ยิงได้จากมือถือ/ป้าย เติมกลับเข้า PD ให้สินค้าที่ PD ยังไม่มีบาร์โค้ด
// (pd-api buy_product_create: sku ที่มีอยู่แล้ว = patch เฉพาะช่องว่าง ไม่ทับของเดิม) — พลาดไม่บล็อกงานซื้อ
async function backfillBarcode(sku, name, barcode) {
  if (!sku || !name || !barcode) return;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await fetch('/api/pd/create-product', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ sku, name, barcode, patch_only: true }),
    });
  } catch { /* PD ล่มชั่วคราว — ข้าม */ }
}

export default function ImportPI({ onGoSuppliers, onGoOrders }) {
  const { hasCostAccess, session } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [currency, setCurrency] = useState('JPY');
  const [fx, setFx] = useState('');
  const [fxAuto, setFxAuto] = useState(false);   // เรทในช่องมาจาก "เรทกลาง" (ยังไม่มีใครพิมพ์ทับ)
  const fxForRef = useRef(null);                  // เรทในช่องเป็นของสกุลไหน (พิมพ์เรท USD แล้วเปลี่ยนเป็นเยน = ต้องเปลี่ยนตาม)
  const fxTable = useFxDefaults();
  const [reference, setReference] = useState('');
  const [orderDate, setOrderDate] = useState(thToday());   // วันไทย (เดิม toISOString = วันที่ UTC)
  const [eta, setEta] = useState('');

  const [step, setStep] = useState(1);           // 1 upload · 1.5 map · 2 review · 3 done
  const [parsed, setParsed] = useState(null);    // { headers, rows }
  const [piFile, setPiFile] = useState(null);    // original file, archived with the PO
  const [mapping, setMapping] = useState({});
  const [knownMapId, setKnownMapId] = useState(null);
  const [lines, setLines] = useState([]);
  const [createIdx, setCreateIdx] = useState(null);
  const [pickIdx, setPickIdx] = useState(null);   // จับคู่สินค้าที่มีอยู่แล้วด้วยมือ (picker กลาง PD-first)
  const [pickQuery, setPickQuery] = useState(null); // คำค้นเริ่มต้นของ picker (null = ใช้รหัสบนใบ)
  const [syncMsg, setSyncMsg] = useState(null);     // ผลเทียบร่างกับ PD ล่าสุด { text, warn }
  const [syncBusy, setSyncBusy] = useState(false);
  const [pdInfo, setPdInfo] = useState({});       // sku → {image_url, default_price, name_*} จาก PD ไว้โชว์ยืนยันว่าลิงก์ถูกตัว
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);        // { poNo, skuCount, newCount }
  const newCount = useRef(0);
  const fileInput = useRef(null);
  const seededSup = useRef(false);   // ตั้งซัพ/สกุลเงินเริ่มต้นแล้วหรือยัง (กันดึงรอบหลังเด้งทับ)

  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [savedMsg, setSavedMsg] = useState('');

  const supplier = suppliers.find(s => s.id === supplierId) ?? null;

  function loadDrafts() {
    supabase.from('pi_drafts')
      .select('id, supplier_id, currency, fx_rate, reference, order_date, file_name, lines, created_at, updated_at')
      .order('updated_at', { ascending: false })
      .then(({ data }) => setDrafts(data ?? []));
  }

  // ---------- PI จากระบบนำเข้า (schema inbound — อ่านอย่างเดียวด้วยสิทธิ์ผู้ใช้) ----------
  const [inbShips, setInbShips] = useState(null);     // shipment ทั้งหมดใน inbound · null = ยังไม่โหลด
  const [inbFilter, setInbFilter] = useState('all');  // all | pi_todo | no_pi | done
  const [inbOpen, setInbOpen] = useState({});         // { [shipmentId]: 'docs' | 'items' } กางดูเอกสาร/สินค้า
  const [inbShowHidden, setInbShowHidden] = useState(false);
  const [inbBusy, setInbBusy] = useState(null);       // document_id ที่กำลัง Process
  const [inbMsg, setInbMsg] = useState('');
  const [inbSync, setInbSync] = useState(false);      // กำลังดึงข้อมูลใหม่จาก inbound
  const [inbAt, setInbAt] = useState(null);           // ดึงล่าสุดเมื่อไหร่

  // Purim 2 ต.ค. 69: โชว์ "ทุก shipment" ใน Inbound (ไม่ใช่เฉพาะไฟล์ PI) — ดูออกว่ามีของอะไรเข้ามาบ้าง
  // shipment ที่มี PI ทำได้แบบเดิม (ดูไฟล์/ดาวน์โหลด/Process/ซ่อน) · ที่ไม่มี PI ก็ขึ้นพร้อมสถานะ/วันของถึง/เอกสาร/สินค้า
  async function loadInbound() {
    setInbSync(true);
    try {
      const inb = supabase.schema('inbound');
      const [{ data: ships, error: e1 }, { data: cons }, { data: docs }, { data: items }, { data: links }] = await Promise.all([
        inb.from('shipment').select('id, code, label, mode, status, etd, eta, created_at').limit(2000),
        inb.from('consignment').select('id, shipment_id, supplier_name, currency, invoice_no, invoice_date').limit(5000),
        inb.from('document').select('id, shipment_id, consignment_id, req_type, file_path, file_name, mime_type, uploaded_at')
          .order('uploaded_at', { ascending: false }).limit(5000),
        inb.from('line_item').select('id, consignment_id, description, sku, qty, unit').limit(10000),
        supabase.from('inbound_pi_links').select('*'),
      ]);
      if (e1) throw e1;
      const consByShip = new Map();
      (cons ?? []).forEach(c => { const a = consByShip.get(c.shipment_id) ?? []; a.push(c); consByShip.set(c.shipment_id, a); });
      const consById = new Map((cons ?? []).map(c => [c.id, c]));
      const shipById = new Map((ships ?? []).map(sh => [sh.id, sh]));
      const linkByDoc = new Map((links ?? []).map(l => [l.document_id, l]));
      const docsByShip = new Map();
      (docs ?? []).forEach(d => { const a = docsByShip.get(d.shipment_id) ?? []; a.push(d); docsByShip.set(d.shipment_id, a); });
      const itemsByShip = new Map();
      (items ?? []).forEach(li => {
        const sid = consById.get(li.consignment_id)?.shipment_id;
        if (!sid) return;
        const a = itemsByShip.get(sid) ?? []; a.push(li); itemsByShip.set(sid, a);
      });
      // ช่วงหลังพนักงานอัปโหลดทุกไฟล์เป็น 'attachment' — ดักชื่อไฟล์เข้าเค้า PI ด้วย (คนตาดูอีกชั้นอยู่แล้ว)
      const isPi = d => d.req_type === 'proforma_invoice'
        || (d.req_type === 'attachment' && /(proforma|(^|[^A-Za-z])PI(?=[^A-Za-z]|$))/i.test(d.file_name || ''));
      setInbShips((ships ?? []).map(sh => {
        const sdocs = docsByShip.get(sh.id) ?? [];
        const scons = consByShip.get(sh.id) ?? [];
        const hasSlip = sdocs.some(d => d.req_type === 'payment_proof');
        const pis = sdocs.filter(isPi).map(d => ({
          ...d,
          ship: shipById.get(d.shipment_id) || null,
          con: (d.consignment_id && consById.get(d.consignment_id)) || scons[0] || null,
          hasSlip,
          link: linkByDoc.get(d.id) || null,
        }));
        // วันที่ใช้เรียง/นับลำดับ = วันของถึง (ETA) → ไม่มีใช้ ETD → ไม่มีใช้วันที่สร้างในระบบ
        const when = sh.eta || sh.etd || thDay(sh.created_at);
        return { ...sh, con: scons[0] || null, cons: scons, docs: sdocs, pis, items: itemsByShip.get(sh.id) ?? [], hasSlip, when };
      }));
      setInbAt(new Date());
    } catch (e) { console.warn('โหลดข้อมูลจาก inbound ไม่ได้:', e?.message ?? e); setInbShips([]); }
    finally { setInbSync(false); }
  }

  // ดึงข้อมูลใหม่จาก inbound ด้วยมือ (พนักงานเพิ่งเพิ่ม shipment / upload PI ระหว่างเปิดหน้าค้างไว้)
  async function syncInbound() {
    if (inbSync) return;
    setInbMsg('');
    const beforeShips = inbShips?.length ?? 0;
    const beforePis = (inbShips ?? []).reduce((t, sh) => t + sh.pis.length, 0);
    await loadInbound();
    setInbShips(cur => {
      const ns = (cur ?? []).length;
      const np = (cur ?? []).reduce((t, sh) => t + sh.pis.length, 0);
      const parts = [];
      if (ns > beforeShips) parts.push(`shipment ใหม่ ${ns - beforeShips}`);
      if (np > beforePis) parts.push(`PI ใหม่ ${np - beforePis}`);
      setInbMsg(parts.length ? `✓ ดึงข้อมูลใหม่แล้ว — พบ ${parts.join(' · ')}` : '✓ ดึงข้อมูลใหม่แล้ว — ยังไม่มีรายการใหม่');
      return cur;
    });
  }

  async function inbView(row) {
    const { data, error } = await supabase.storage.from('inbound-docs').createSignedUrl(row.file_path, 600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    else setInbMsg('✗ เปิดไฟล์ไม่ได้: ' + (error?.message ?? 'ไม่ทราบสาเหตุ'));
  }
  // ดาวน์โหลดไฟล์ต้นฉบับ — signed URL แบบ download ให้ไฟล์ออกมาชนิด/นามสกุลเดิม (PDF ก็ PDF, รูปก็รูป)
  async function inbDownload(row) {
    const base = row.file_path.split('/').pop() || 'document';
    const name = /\.[A-Za-z0-9]{2,5}$/.test(row.file_name || '') ? row.file_name : base;
    const { data, error } = await supabase.storage.from('inbound-docs')
      .createSignedUrl(row.file_path, 600, { download: name });
    if (!data?.signedUrl) { setInbMsg('✗ ดาวน์โหลดไม่ได้: ' + (error?.message ?? 'ไม่ทราบสาเหตุ')); return; }
    const a = document.createElement('a');
    a.href = data.signedUrl; a.download = name; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  }
  async function inbHide(row, hide) {
    await supabase.from('inbound_pi_links').upsert({
      document_id: row.id, status: hide ? 'hidden' : 'new',
      file_name: row.file_name ?? null, shipment_code: row.ship?.code ?? null,
      updated_at: new Date().toISOString(),
    });
    loadInbound();
  }
  async function inbProcess(row) {
    if (inbBusy) return;
    setInbBusy(row.id); setInbMsg('');
    try {
      const out = await authFetch('/api/inbound/process', {
        method: 'POST', body: JSON.stringify({ document_id: row.id }),
      });
      setInbMsg(`✓ สร้างร่างแล้ว — ${out.supplier} · ${out.lines} รายการ (จับคู่สินค้าได้ ${out.matched}) · ${out.in_number} — เปิดจาก "ร่างที่ค้างไว้" ด้านบนได้เลย`);
      loadDrafts(); loadInbound();
    } catch (e) { setInbMsg('✗ ' + (e.message ?? e)); }
    finally { setInbBusy(null); }
  }

  function loadSuppliers() {
    return supabase.from('suppliers')
      .select('id, company_name, currency, default_brand:brands!suppliers_default_brand_id_fkey(id, name)')
      .eq('is_active', true).order('company_name')
      .then(({ data }) => {
        if (!data) return;                       // โหลดพลาดชั่วคราว = คงรายชื่อเดิมไว้ ดีกว่าล้างทิ้ง
        setSuppliers(data);
        // ตั้งค่าเริ่มต้นครั้งแรกครั้งเดียว — ดึงรอบหลังต้องไม่เด้งทับที่ผู้ใช้เลือกไว้
        if (!seededSup.current && data.length) {
          seededSup.current = true;
          setSupplierId(data[0].id);
          setCurrency(data[0].currency);
        }
      });
  }

  useEffect(() => {
    loadSuppliers();
    loadDrafts();
    loadInbound();
    // ซัพพลายเออร์เพิ่มได้จากหน้าอื่น/เครื่องอื่น — ดึงรายชื่อใหม่ทุกครั้งที่กลับมาที่แท็บนี้
    // (เคสจริง 13 ก.ย. 69: เพิ่มซัพใหม่แล้วแท็บที่เปิดค้างไว้ไม่เห็นเจ้าใหม่ เพราะโหลดครั้งเดียวตอน mount)
    const refresh = () => { if (document.visibilityState === 'visible') loadSuppliers(); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  function pickSupplier(id) {
    setSupplierId(id);
    const s = suppliers.find(x => x.id === id);
    if (s?.currency) setCurrency(s.currency);
  }

  async function onFile(file) {
    if (!file || !supplier) { setErr(!supplier ? 'เลือกซัพพลายเออร์ก่อน' : ''); return; }
    setErr(''); setBusy(true);
    try {
      const p = await parseFile(file);
      setParsed(p);
      setPiFile(file);
      const fp = fingerprint(p.headers);
      const { data: maps } = await supabase.from('supplier_column_maps')
        .select('id, file_signature, mapping, is_complete')
        .eq('supplier_id', supplier.id).order('updated_at', { ascending: false });
      const known = (maps ?? []).find(m => m.file_signature === fp);
      if (known?.is_complete) {
        // remembered layout → skip mapping, straight to review
        setMapping(known.mapping); setKnownMapId(known.id);
        await toReview(p, known.mapping);
      } else {
        const base = known?.mapping ?? (maps?.[0]?.mapping ?? {});
        const prefill = {};
        for (const h of p.headers) if (base[h]) prefill[h] = base[h];
        setMapping(Object.keys(prefill).length ? prefill : guessMapping(p.headers));
        setKnownMapId(known?.id ?? null);
        setStep(1.5);
      }
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function toReview(p = parsed, m = mapping) {
    setBusy(true); setErr('');
    try {
      const all = applyMapping(p.headers, p.rows, m);
      if (!all.length) throw new Error('ไม่พบรายการสินค้าในไฟล์ — ตรวจการจับคู่คอลัมน์');
      const products = all.filter(l => l.lineType === 'product');
      const fees = all.filter(l => l.lineType === 'fee');
      const matched = products.length ? await matchLines(supplier.id, products) : [];
      const combined = [...matched, ...fees.map(f => ({ ...f, product: null, source: null }))];
      newCount.current = 0;
      setLines(combined.map(l => ({ ...l, excluded: false })));
      setStep(2);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  // ดึงรูป+ราคาขาย+ราคาซื้อล่าสุดจาก PD มาแปะแถวที่เชื่อมแล้ว — ให้เห็นชัดว่าลิงก์ถูกตัว
  // ผ่าน /api/pd/costs (ติดสิทธิ์ cost role) เพราะ purchase_cost เป็นข้อมูลต้นทุน ห้ามออกทาง view สาธารณะ
  useEffect(() => {
    const skus = [...new Set(lines.filter(l => l.product?.sku).map(l => l.product.sku))]
      .filter(s => !(s in pdInfo));
    if (!skus.length) return;
    authFetch('/api/pd/costs', { method: 'POST', body: JSON.stringify({ skus: skus.slice(0, 300) }) })
      .then(o => {
        const found = o.rows ?? {};
        // sku ที่ PD ไม่มี = จำเป็น null กันวน fetch ซ้ำ
        setPdInfo(m => ({ ...m, ...Object.fromEntries(skus.map(s => [s, found[s] ?? null])) }));
      })
      .catch(() => setPdInfo(m => ({ ...m, ...Object.fromEntries(skus.map(s => [s, null])) })));
  }, [lines]);

  // ⚠️ ต้องประกาศก่อน useEffect ที่ใช้ fxNum ข้างล่าง — dependency array ถูกอ่านตอน render
  // ถ้าไปประกาศทีหลัง จะ ReferenceError (TDZ) ทั้งหน้า = จอขาว
  const fxNum = parseFloat(fx) || 0;

  // เรทกลาง (Purim 1 ต.ค. 69): ใบที่เรทว่าง — โดยเฉพาะร่างจาก Inbound ที่ AI สร้างมาแบบไม่มีเรท —
  // เติมค่ากลางของสกุลนั้นให้เอง (แก้ทับได้เสมอ) · เดิมว่างแล้วใบถูกยืนยันไปด้วยเรทว่าง = ต้นทุนไม่เข้า PD
  // เปลี่ยนสกุลตอนเรทยังเป็นค่ากลาง = เปลี่ยนตามสกุลใหม่ · สกุลที่ไม่มีค่ากลาง = ล้างทิ้ง (กันใช้เรทผิดสกุล)
  useEffect(() => {
    if (!fxTable) return;
    const cur = (currency || '').toUpperCase();
    const def = fxDefaultOf(cur, fxTable);
    const otherCurrency = fxForRef.current && fxForRef.current !== cur;   // เรทที่อยู่ในช่องเป็นของสกุลเดิม
    if (!String(fx).trim() || fxAuto || otherCurrency) {
      const next = def != null ? String(def) : '';
      if (next !== String(fx)) setFx(next);
      setFxAuto(def != null);
      fxForRef.current = cur;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, fxTable]);   // ไม่ผูกกับ fx: ผู้ใช้ลบช่องเองต้องว่างได้ (ยืนยันใบจะถูกบล็อกให้ใส่เรทแทน)
  const fxDefault = fxDefaultOf(currency, fxTable);
  const onFxInput = v => { setFx(v); setFxAuto(false); fxForRef.current = (currency || '').toUpperCase(); };
  const fxWarn = (currency || '').toUpperCase() !== 'THB' ? fxRangeWarn(currency, fxNum) : '';

  // เติมราคาซื้อจาก "ซื้อล่าสุด" ใน PD ให้บรรทัดที่ราคาเป็น 0 (ใบจาก by Photos มักไม่มีราคาบนป้าย)
  // THB = ใช้ตรง ๆ · สกุลอื่นต้องมี FX ถึงแปลงกลับได้ · ไม่ทับราคาที่มีอยู่แล้ว — ใบสร้างแล้วยังแก้ได้ในลิ้นชักใบ
  useEffect(() => {
    const cur = (currency || '').toUpperCase();
    let changed = 0;
    const next = lines.map(l => {
      if (l.lineType !== 'product' || l._costFrom || !l.product?.sku || (parseFloat(l.unit) || 0) > 0) return l;
      const cost = Number(pdInfo[l.product.sku]?.purchase_cost);
      if (!(cost > 0)) return l;
      let unit = null;
      if (cur === 'THB') unit = cost;
      else if (fxNum > 0) unit = Math.round((cost / fxNum) * 100) / 100;
      if (unit == null) return l;
      changed++;
      return { ...l, unit, amount: null, _costFrom: 'pd' };   // amount=null → คิดจาก qty×unit ใหม่
    });
    if (changed) setLines(next);
  }, [pdInfo, lines, fxNum, currency]);

  // ── ของที่มาเป็นกล่อง (เช่น Nalgene 1 กล่อง = 6 ขวด ราคาบนใบก็เป็นต่อกล่อง) ──
  // SKU ของเราคือ "ขวด" → ต้องบันทึกเป็นจำนวนขวด + ราคาต่อขวด ไม่งั้น
  // สต๊อกจะขาดเป็นเท่าตัว และต้นทุน/ชิ้นที่ไหลเข้า PD จะสูงเกินจริงเท่าจำนวนต่อกล่อง
  // เก็บ qty/unit ไว้ "ตามใบ PI" แล้วคูณ/หารตอนบันทึกจริง — กระทบยอดกับใบเรียกเก็บเงินได้
  function packOf(l) { const n = parseInt(l.pack, 10); return Number.isFinite(n) && n > 1 ? n : 1; }
  function realQty(l) { return (Number(l.qty) || 0) * packOf(l); }
  function realUnit(l) {
    const p = packOf(l), u = Number(l.unit) || 0;
    return p > 1 ? Math.round((u / p) * 10000) / 10000 : u;
  }

  // แก้ตัวเลขในหน้าตรวจสอบ (จำนวน/ราคา/ต่อกล่อง) — แก้ qty หรือ unit แล้วต้องล้าง amount
  // ที่อ่านมาจากใบ ไม่งั้นช่อง "รวม" ค้างเลขเดิมไม่ตรงกับที่แก้
  function setLineNum(i, field, value) {
    setLines(ls => ls.map((l, j) => {
      if (j !== i) return l;
      const next = { ...l, [field]: value };
      if (field === 'qty' || field === 'unit') next.amount = null;
      if (field === 'pack') next._packFrom = null;   // แก้เองแล้ว ไม่ใช่ค่าที่จำมา
      return next;
    }));
  }
  // ทั้งใบมักเป็นขนาดเดียวกัน (Nalgene = 6 ทุกรุ่น) — ตั้งทีเดียวจบ เฉพาะบรรทัดสินค้า
  function setAllPacks(v) {
    setLines(ls => ls.map(l => l.lineType === 'product' ? { ...l, pack: v, _packFrom: null } : l));
  }

  function toggleExcluded(i) { setLines(ls => ls.map((l, j) => j === i ? { ...l, excluded: !l.excluded } : l)); }
  function setLineCategory(i, key) {
    setLines(ls => ls.map((l, j) => j === i
      ? (key === 'product'
          ? { ...l, lineType: 'product', feeCategory: null }
          : key === 'claim'
          // สินค้าเครม: ยังเป็นสินค้า (ต้องเชื่อมสินค้า + เข้าสต๊อก) แต่ราคา 0 ไม่คิดมูลค่าซื้อ
          ? { ...l, lineType: 'product', feeCategory: 'claim', unit: 0, amount: 0 }
          : { ...l, lineType: 'fee', feeCategory: key })
      : l));
  }
  const lineCat = l => l.lineType === 'fee' ? (l.feeCategory ?? 'other') : (l.feeCategory === 'claim' ? 'claim' : 'product');
  const catTh = key => LINE_CATEGORIES.find(c => c.key === key)?.th ?? key;

  async function saveDraft() {
    if (busy || !lines.length || !supplier) return;
    setBusy(true); setErr('');
    try {
      const payload = {
        user_id: session.user.id,
        supplier_id: supplier.id,
        currency, fx_rate: fx, reference: reference.trim() || null,
        order_date: orderDate || null, eta: eta || null,
        file_name: piFile?.name ?? null,
        parsed: parsed ? { headers: parsed.headers, rows: parsed.rows } : null,
        mapping,
        lines: lines.map(l => ({
          code: l.code, desc: l.desc, qty: l.qty, unit: l.unit, amount: l.amount, pack: l.pack ?? 1,
          lineType: l.lineType, feeCategory: l.feeCategory ?? null, excluded: l.excluded,
          product: l.product ? { id: l.product.id, name: l.product.name, sku: l.product.sku, image_url: l.product.image_url } : null,
          source: l.source ?? null,
        })),
        updated_at: new Date().toISOString(),
      };
      if (draftId) {
        const { error } = await supabase.from('pi_drafts').update(payload).eq('id', draftId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('pi_drafts').insert(payload).select('id').single();
        if (error) throw error;
        setDraftId(data.id);
      }
      loadDrafts();
      setSavedMsg('บันทึกร่างแล้ว — กลับมาทำต่อได้ทุกเมื่อ');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) { setErr(e.message ?? String(e)); }
    finally { setBusy(false); }
  }

  async function resumeDraft(d) {
    setBusy(true); setErr('');
    try {
      const { data: full, error } = await supabase.from('pi_drafts').select('*').eq('id', d.id).single();
      if (error) throw error;
      const s = suppliers.find(x => x.id === full.supplier_id);
      if (s) { setSupplierId(s.id); setCurrency(full.currency || s.currency); }
      // ร่างที่มีเรทแล้ว = ของคนกรอก ห้ามค่ากลางทับ · ร่างเรทว่าง (เช่นจาก Inbound) = เติมค่ากลางของสกุลนั้น
      const draftCur = full.currency || s?.currency || currency;
      const def = fxDefaultOf(draftCur, fxTable);
      fxForRef.current = String(draftCur || '').toUpperCase();
      if (full.fx_rate != null && String(full.fx_rate) !== '') { setFx(String(full.fx_rate)); setFxAuto(false); }
      else { setFx(def != null ? String(def) : ''); setFxAuto(def != null); }
      setReference(full.reference ?? '');
      setOrderDate(full.order_date ?? thToday());
      setEta(full.eta ?? '');
      setParsed(full.parsed ?? null);
      setMapping(full.mapping ?? {});
      // ร่างเก็บสำเนาสินค้า ณ ตอนสร้าง — PD อาจแก้ไปแล้ว (เปลี่ยนรหัส/ลบตัวซ้ำ) เทียบใหม่ทุกครั้งที่เปิดร่าง
      let draftLines = full.lines ?? [];
      try {
        const r = await refreshLines(full.supplier_id, draftLines);
        draftLines = r.lines;
        setSyncMsg(syncSummary(r.stats, true));
      } catch (e) { setSyncMsg({ text: 'เทียบกับ PD ไม่สำเร็จ — กด "ดึงข้อมูลสินค้าล่าสุดจาก PD" อีกครั้ง', warn: true }); }
      setLines(draftLines);
      setDraftId(full.id);
      newCount.current = 0;
      setStep(2);
    } catch (e) { setErr(e.message ?? String(e)); }
    finally { setBusy(false); }
  }

  async function deleteDraft(id) {
    await supabase.from('pi_drafts').delete().eq('id', id);
    setDrafts(ds => ds.filter(d => d.id !== id));
    if (draftId === id) setDraftId(null);
  }

  const active = lines.filter(l => !l.excluded);
  const activeProducts = active.filter(l => l.lineType === 'product');
  const linked = activeProducts.filter(l => l.product).length;
  const needed = activeProducts.length - linked;
  // บรรทัดสินค้าที่ผูกกับสินค้าตัวเดียวกัน (สีต่างกันแต่จับไปตัวเดียว = พลาดง่ายถ้าไม่ได้ตรวจ)
  const dupGroups = useMemo(() => {
    const m = new Map();
    lines.forEach((l, i) => {
      if (l.excluded || l.lineType !== 'product' || !l.product?.id) return;
      const g = m.get(l.product.id) ?? { sku: l.product.sku, name: l.product.name, idx: [], codes: [] };
      g.idx.push(i); g.codes.push(l.code); m.set(l.product.id, g);
    });
    return [...m.values()].filter(g => g.idx.length > 1);
  }, [lines]);
  const dupIdx = new Set(dupGroups.flatMap(g => g.idx));
  // ยอดรวม = ตามใบ PI เสมอ (กล่อง×ราคา/กล่อง = ชิ้น×ราคา/ชิ้น เท่ากัน) — แก้ช่องไหนก็คิดใหม่ให้
  const totForeign = useMemo(
    () => active.reduce((t, l) => t + (l.amount ?? (Number(l.qty) || 0) * (Number(l.unit) || 0)), 0),
    [lines]);
  const sym = CUR_SYM[currency?.toUpperCase()] ?? '';

  // ---------- ให้ร่างตรงกับ PD เสมอ (Purim 1 ต.ค. 69: "แก้ใน PD แล้ว แต่ใน BUY ไม่อัปเดตตาม") ----------
  function syncSummary(st, onOpen) {
    const parts = [];
    if (st.rematched) parts.push(`จับคู่ใหม่ได้ ${st.rematched} รายการ`);
    if (st.updated) parts.push(`ข้อมูลสินค้าเปลี่ยนตาม PD ${st.updated} รายการ`);
    if (st.dropped) parts.push(`สินค้าที่เคยจับคู่ถูกลบจาก PD ${st.dropped} รายการ — ต้องจับคู่ใหม่`);
    if (st.stillMissing) parts.push(`ยังหาไม่เจอ ${st.stillMissing} รายการ`);
    if (!parts.length) return onOpen ? null : { text: 'ตรงกับ PD ทุกรายการแล้ว ✓', warn: false };
    return { text: `อัปเดตจาก PD: ${parts.join(' · ')}`, warn: !!(st.dropped || st.stillMissing) };
  }
  async function syncFromPd() {
    if (syncBusy || !supplier) return;
    setSyncBusy(true); setErr('');
    try {
      const r = await refreshLines(supplier.id, lines);
      setLines(r.lines);
      setSyncMsg(syncSummary(r.stats, false));
    } catch (e) { setErr(e.message ?? String(e)); }
    finally { setSyncBusy(false); }
  }
  // ก่อนสร้างสินค้าใหม่: ค้น PD ด้วยชื่อบนใบก่อน — กันสร้างตัวซ้ำ (เคส 30 ก.ย. หาไม่เจอเพราะรหัสไม่ตรง
  // เลยสร้าง Ti-Single Mug 300 Cover DARK OLIVE ขึ้นมาอีกตัว = สินค้าซ้อน 2 ตัว)
  async function guardCreate(i) {
    const l = lines[i];
    const q = String(l?.desc || '').trim();
    let hits = [];
    if (q.length >= 3) {
      try { hits = (await authFetch(`/api/pd/search?q=${encodeURIComponent(q)}`, { method: 'GET' })).rows || []; }
      catch { hits = []; }
    }
    if (!hits.length) { setCreateIdx(i); return; }
    const list = hits.slice(0, 6).map(h => `• ${h.sku} — ${h.name}`).join('\n');
    const more = hits.length > 6 ? `\n… และอีก ${hits.length - 6} ตัว` : '';
    if (window.confirm(`มีสินค้าชื่อคล้าย "${q}" อยู่ใน PD แล้ว ${hits.length} ตัว:\n${list}${more}\n\n`
      + 'ถ้าเป็นตัวเดียวกัน กด Cancel เพื่อเลือกจับคู่ตัวเดิม (ไม่สร้างซ้ำ)\nถ้าเป็นสินค้าใหม่จริง ๆ กด OK เพื่อสร้าง')) {
      setCreateIdx(i);
    } else {
      setPickQuery(q); setPickIdx(i);
    }
  }

  async function complete() {
    if (busy || needed > 0 || !active.length) return;
    // สินค้าเดียวกันถูกจับคู่หลายบรรทัด = มักเป็นการจับผิดตัว (เช่นสีต่างกันไปผูกตัวเดียวกัน) — ถามก่อน
    if (dupGroups.length && !window.confirm('⚠ สินค้าเดียวกันถูกจับคู่มากกว่า 1 บรรทัด:\n'
      + dupGroups.map(g => `• ${g.sku} ← รหัสบนใบ ${g.codes.join(', ')}`).join('\n')
      + '\n\nถ้าตั้งใจ (เช่นของล็อตแยก) กด OK เพื่อยืนยันใบ · ถ้าจับคู่ผิด กด Cancel แล้วแก้ก่อน')) return;
    // ห้ามยืนยันใบสกุลต่างประเทศแบบเรทว่าง (เคส PO-2026-080004: เรทว่าง → มูลค่า ฿0 + ต้นทุนไม่เข้า PD + รับของแล้วทุนว่าง)
    if ((currency || '').toUpperCase() !== 'THB' && !(fxNum > 0)) {
      setErr(`ใบนี้เป็นสกุล ${currency} แต่ยังไม่มีเรท FX — ใส่เรทที่หัวหน้านี้ก่อน (หรือตั้ง "เรทค่าเงินกลาง" ของ ${currency} ในหน้าแรก)`);
      return;
    }
    setBusy(true); setErr('');
    try {
      const productLines = active.filter(l => l.lineType === 'product');
      const feeLines = active.filter(l => l.lineType === 'fee');

      // กันสร้างใบซ้ำ (บั๊ก 1 ส.ค.: ยืนยันพลาดกลางทางแล้วกดซ้ำ = ได้ใบใหม่ทุกครั้ง — เกิด 3 ใบซ้อน)
      // เลขอ้างอิง PI เดียวกันที่ยังไม่ถูกเก็บ = ใบเดียวพอ; ถ้าต้องสร้างใหม่จริงให้ archive ใบเดิมก่อน
      const refKey = reference.trim();
      if (refKey) {
        const { data: dupPo } = await supabase.from('purchase_orders')
          .select('po_no').eq('reference', refKey).eq('archived', false).limit(1).maybeSingle();
        if (dupPo) throw new Error(`ใบ PI "${refKey}" มีใบสั่งซื้อแล้ว (${dupPo.po_no}) — ไม่สร้างซ้ำ · ถ้าต้องการสร้างใหม่ ให้เก็บใบเดิมเข้าคลังเอกสารก่อน`);
      }

      const { data: po, error: poErr } = await supabase.from('purchase_orders').insert({
        status: 'ordered',
        created_at: orderDate || undefined,
        supplier_id: supplier.id,
        brand_name: supplier.default_brand?.name ?? null,
        currency: currency.toUpperCase(),
        fx_rate: fxNum || null,
        reference: reference.trim() || null,
        expected_at: eta || null,
        total_qty: productLines.reduce((t, l) => t + realQty(l), 0),
        total_amount: Math.round(totForeign * fxNum * 100) / 100,
        notes: null,   // ช่องหมายเหตุ = ของ Purim จดเอง (โชว์ในลิสต์) — เลิกใส่ข้อความระบบทับ
      }).select('id, po_no').single();
      if (poErr) throw poErr;

      const items = [
        ...productLines.map(l => ({
          purchase_order_id: po.id,
          product_id: l.product.id,
          product_name_snapshot: l.product.name,
          brand_name_snapshot: supplier.default_brand?.name ?? null,
          sku_snapshot: l.product.sku,
          // มาเป็นกล่อง → เก็บเป็นจำนวนชิ้นจริง + ราคาต่อชิ้น (ยอดรวมเท่าใบ PI เท่าเดิม)
          quantity: realQty(l),
          unit_price: 0,
          supplier_product_code: l.code,
          unit_price_foreign: realUnit(l),
          provisional_unit_cost: fxNum ? Math.round(realUnit(l) * fxNum * 100) / 100 : null,
          status: 'ordered',
          // เก็บเลขเดิมบนใบไว้ กระทบยอดกับใบเรียกเก็บเงินของซัพได้
          notes: packOf(l) > 1
            ? `ตามใบ PI: ${fmt(l.qty)} กล่อง × ${packOf(l)} ชิ้น @ ${sym}${fmt(l.unit, 2)}/กล่อง`
            : null,
          line_type: 'product',
          fee_category: l.feeCategory === 'claim' ? 'claim' : null,
        })),
        ...feeLines.map(l => ({
          purchase_order_id: po.id,
          product_id: null,
          product_name_snapshot: l.desc || l.code,
          brand_name_snapshot: null,
          sku_snapshot: null,
          quantity: l.qty || 1,
          unit_price: 0,
          supplier_product_code: l.code || null,
          unit_price_foreign: l.unit ?? l.amount,
          provisional_unit_cost: fxNum ? Math.round((l.unit ?? l.amount) * fxNum * 100) / 100 : null,
          status: 'ordered',
          line_type: 'fee',
          fee_category: l.feeCategory ?? 'other',
        })),
      ];
      const { error: itErr } = await supabase.from('purchase_order_items').insert(items);
      if (itErr) throw itErr;

      // remember every code → product mapping (product lines only) — non-fatal: ใบสร้างแล้ว
      // ⚠️ ใบอาจมีรหัสเดิมหลายบรรทัด (เช่นแยกล็อต) — ต้อง dedupe ก่อน upsert
      // ไม่งั้น Postgres ตอบ "ON CONFLICT DO UPDATE cannot affect row a second time" (บั๊ก 1 ส.ค.)
      if (productLines.length) {
        const byCode = new Map();
        for (const l of productLines) if (l.code) byCode.set(l.code, l);
        const { error: spErr } = await supabase.from('supplier_products').upsert(
          [...byCode.values()].map(l => ({
            supplier_id: supplier.id,
            supplier_product_code: l.code,
            product_id: l.product.id,
            last_unit_cost_foreign: realUnit(l),   // ต่อชิ้นเสมอ (ไม่ใช่ต่อกล่อง)
            pack_size: packOf(l),                  // จำไว้ → ครั้งหน้าเติมช่อง "ต่อกล่อง" ให้เอง
            last_currency: currency.toUpperCase(),
            last_seen_at: new Date().toISOString(),
          })), { onConflict: 'supplier_id,supplier_product_code' });
        if (spErr) console.warn('จำรหัสซัพพลายเออร์ไม่สำเร็จ (ไม่กระทบใบ):', spErr.message);
      }

      // remember the column layout for next time — non-fatal เช่นกัน
      try {
        const mapRow = {
          supplier_id: supplier.id,
          file_signature: fingerprint(parsed.headers),
          mapping,
          is_complete: isComplete(mapping),
          updated_at: new Date().toISOString(),
        };
        const { error: mErr } = knownMapId
          ? await supabase.from('supplier_column_maps').update(mapRow).eq('id', knownMapId)
          : await supabase.from('supplier_column_maps').insert(mapRow);
        if (mErr) console.warn('จำผังคอลัมน์ไม่สำเร็จ (ไม่กระทบใบ):', mErr.message);
      } catch (e) { console.warn('จำผังคอลัมน์ไม่สำเร็จ (ไม่กระทบใบ):', e?.message ?? e); }

      // archive the original PI file (private bucket, cost roles only) — non-fatal
      if (piFile) {
        const safe = piFile.name.replace(/[^A-Za-z0-9._-]/g, '_');
        const path = `pi/${po.id}-${safe}`;
        const { error: upErr } = await supabase.storage.from('sourcepoint-docs').upload(path, piFile);
        if (!upErr) await supabase.from('purchase_orders').update({ pi_file_path: path }).eq('id', po.id);
      } else if (draftId) {
        // ร่างจากระบบนำเข้า (Inbound): ใช้สำเนา PDF ที่คัดไว้แล้วเป็นไฟล์ PI ของใบ — non-fatal
        try {
          const { data: lk } = await supabase.from('inbound_pi_links')
            .select('pi_file_copy').eq('pi_draft_id', draftId).maybeSingle();
          if (lk?.pi_file_copy) await supabase.from('purchase_orders').update({ pi_file_path: lk.pi_file_copy }).eq('id', po.id);
        } catch (e) { console.warn('ผูกไฟล์ PI จาก inbound ไม่สำเร็จ (ไม่กระทบใบ):', e?.message ?? e); }
      }

      if (draftId) {
        // ร่างจาก Inbound: จดเลขใบลงตารางสถานะ ให้ลิสต์โชว์ "✓ ทำแล้ว · PO-xxxx" — non-fatal
        try {
          await supabase.from('inbound_pi_links')
            .update({ po_id: po.id, po_no: po.po_no ?? null, updated_at: new Date().toISOString() })
            .eq('pi_draft_id', draftId);
        } catch (e) { console.warn('อัปเดตสถานะ inbound ไม่สำเร็จ (ไม่กระทบใบ):', e?.message ?? e); }
        await supabase.from('pi_drafts').delete().eq('id', draftId);
        setDraftId(null);
        loadDrafts();
        loadInbound();
      }

      // ส่งใบเข้า PD อัตโนมัติ (PD บันทึก + อัปเดตต้นทุน + relay เข้า Zort เป็น backup) — non-fatal
      // พลาดตรงนี้ยังกดส่งซ้ำได้จากหน้าใบสั่งซื้อ
      let pdPush = null;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const res = await fetch('/api/pd/push-po', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ po_id: po.id }),
          });
          const out = await res.json().catch(() => ({}));
          pdPush = res.ok ? out : { error: out.error || 'ส่งเข้า PD ไม่สำเร็จ' };
        }
      } catch (e) {
        pdPush = { error: e.message ?? String(e) };
      }

      setDone({ poNo: po.po_no, skuCount: productLines.length, feeCount: feeLines.length, newCount: newCount.current, pdPush });
      setStep(3);
    } catch (e) {
      setErr(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  function resetAll() {
    setStep(1); setParsed(null); setPiFile(null); setMapping({}); setKnownMapId(null);
    setLines([]); setDone(null); setErr(''); setSavedMsg(''); setReference(''); setEta('');
    setDraftId(null);
    newCount.current = 0;
    if (fileInput.current) fileInput.current.value = '';
    loadDrafts();
  }

  if (!hasCostAccess) {
    return (
      <div className="todo-page">
        การนำเข้า PI เปิดเฉพาะ role จัดซื้อ (purchasing) / ผู้จัดการ (manager) —
        บัญชีนี้ยังไม่มีสิทธิ์ ติดต่อผู้ดูแลเพื่อปรับ role
      </div>
    );
  }

  return (
    <>
      <div className="stepper">
        {[[1, 'อัปโหลด'], [2, 'ตรวจสอบ'], [3, 'เสร็จสิ้น']].map(([n, l]) => (
          <div key={n} className={`step ${Math.floor(step) === n || (n === 1 && step === 1.5) ? 'active' : step > n ? 'done' : ''}`}>
            <span className="n">{String(n).padStart(2, '0')}</span><span className="l">{l}</span>
          </div>
        ))}
      </div>

      {/* ============ STEP 1 : UPLOAD ============ */}
      {step === 1 && (
        <section>
          <div className="hero">
            <div className="eyebrow">by PI · New import</div>
            <h1>วาง PI ลงมา<br />ระบบจับคู่ให้อัตโนมัติ</h1>
            <p className="sub">อัปโหลดใบ PI จากซัพพลายเออร์ต่างประเทศ ระบบจะอ่านทีละรายการ จับคู่กับสินค้าในระบบ และแจ้งรายการใหม่ให้สร้างก่อนกลายเป็นใบสั่งซื้อ</p>
          </div>

          <div className="grid-upload">
            <div>
              <div className="field">
                <div className="lbl"><span className="th">ซัพพลายเออร์</span><span className="en">Supplier</span></div>
                <select className="under" value={supplierId} onChange={e => pickSupplier(e.target.value)}>
                  {!suppliers.length && <option value="">— ยังไม่มีซัพพลายเออร์ —</option>}
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.company_name}{s.default_brand?.name ? ` — ${s.default_brand.name}` : ''}
                    </option>
                  ))}
                </select>
                <p className="sd-empty" style={{ marginTop: 8 }}>
                  ไม่มีในลิสต์? <a style={{ color: 'var(--ink)', cursor: 'pointer' }} onClick={onGoSuppliers}>+ เพิ่มที่หน้าซัพพลายเออร์</a>
                </p>
              </div>
              <div className="fx-row">
                <div className="field">
                  <div className="lbl"><span className="th">สกุลเงิน</span><span className="en">Currency</span></div>
                  <input className="under mono" value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} />
                </div>
                <div className="field">
                  <div className="lbl"><span className="th">อัตราแลกเปลี่ยน → บาท</span><span className="en">FX → THB</span>
                    {fxAuto && <span className="opt fx-auto">ค่ากลาง</span>}</div>
                  <input className="under mono" inputMode="decimal" value={fx} onChange={e => onFxInput(e.target.value)}
                         placeholder={fxDefault ? `ค่ากลาง ${fxDefault}` : 'เช่น 0.2350'} />
                  {fxWarn && <p className="fx-warn">⚠ {fxWarn}</p>}
                </div>
              </div>
              <FxDefaultsBar highlight={currency?.toUpperCase()} />
              <div className="fx-row">
                <div className="field">
                  <div className="lbl"><span className="th">เลขที่ PI / PO</span><span className="en">Reference</span></div>
                  <input className="under mono" value={reference} onChange={e => setReference(e.target.value)} placeholder="SP-PI-2026-…" />
                </div>
                <div className="field">
                  <div className="lbl"><span className="th">วันที่สั่งซื้อ</span><span className="en">Order date</span><span className="opt">ย้อนหลังได้</span></div>
                  <input className="under mono" type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} />
                </div>
              </div>
              <div className="fx-row">
                <div className="field">
                  <div className="lbl"><span className="th">ของถึงประมาณ</span><span className="en">ETA</span><span className="opt">ไม่บังคับ</span></div>
                  <input className="under mono" type="date" value={eta} onChange={e => setEta(e.target.value)} />
                </div>
                <div />
              </div>
            </div>

            <div>
              <div className="lbl" style={{ marginBottom: 10 }}><span className="th">ไฟล์ใบ PI</span><span className="en">Proforma invoice</span></div>
              <label className="dropzone"
                     onDragOver={e => e.preventDefault()}
                     onDrop={e => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}>
                <input ref={fileInput} type="file" hidden accept=".xlsx,.xls,.csv,.pdf"
                       onChange={e => onFile(e.target.files?.[0])} />
                <div>
                  <div className="ic">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 16V4M12 4L7 9M12 4l5 5" stroke="#0A0A0A" strokeWidth="1.6"/><path d="M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
                  </div>
                  <h3>{busy ? 'กำลังอ่านไฟล์…' : 'ลากไฟล์มาวาง หรือเลือกไฟล์'}</h3>
                  <p>Excel, CSV หรือ PDF — ไม่เกิน 10 MB (PDF ต้องเป็นไฟล์จากระบบ ไม่ใช่สแกน)</p>
                </div>
              </label>
              <div className="dz-note">
                <span>ระบบจำรูปแบบคอลัมน์ของซัพพลายเออร์แต่ละราย</span>
              </div>
              {parsed && lines.length > 0 && (
                <div className="back-row" style={{ paddingTop: 14 }}>
                  <a onClick={() => setStep(2)}>ไฟล์เดิมยังอยู่ ({active.length} รายการ) — กลับไปตรวจสอบต่อ →</a>
                </div>
              )}
              <p className="err-note">{err}</p>
            </div>
          </div>

          {drafts.length > 0 && (
            <div className="drafts-section">
              <div className="eyebrow" style={{ marginBottom: 16 }}>ร่างที่ค้างไว้ · Saved drafts</div>
              <div className="drafts-grid">
                {drafts.map(d => {
                  const ds = suppliers.find(s => s.id === d.supplier_id);
                  const lineCount = Array.isArray(d.lines) ? d.lines.length : 0;
                  const pendingCount = Array.isArray(d.lines) ? d.lines.filter(l => !l.excluded && l.lineType === 'product' && !l.product).length : 0;
                  return (
                    <div className="draft-card" key={d.id}>
                      <div className="draft-head">
                        <span className="draft-supplier">{ds?.company_name ?? '—'}</span>
                        {d.reference && <span className="draft-ref mono">{d.reference}</span>}
                      </div>
                      <div className="draft-meta">
                        <span>{lineCount} รายการ</span>
                        {pendingCount > 0 && <span className="draft-pending">ต้องสร้างอีก {pendingCount}</span>}
                        <span>{d.currency}{d.fx_rate ? ` · FX ${d.fx_rate}` : ''}</span>
                      </div>
                      <div className="draft-date">บันทึก {new Date(d.updated_at).toLocaleDateString('th-TH', { timeZone: TZ, day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                      <div className="draft-actions">
                        <button className="ghost-btn fill" onClick={() => resumeDraft(d)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="currentColor" strokeWidth="1.6"/></svg>
                          ทำต่อ
                        </button>
                        <button className="ghost-btn" onClick={() => deleteDraft(d.id)}>ลบ</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ============ Shipment ทั้งหมดจากระบบนำเข้า (Inbound) — อ่านอย่างเดียว · มี PI กด Process สร้างร่างได้ ============ */}
          {inbShips !== null && (() => {
            const piState = sh => {
              const live = sh.pis.filter(p => p.link?.status !== 'hidden');
              if (!sh.pis.length) return 'no_pi';
              if (live.length && live.every(p => p.link?.status === 'processed')) return 'done';
              if (live.some(p => p.link?.status !== 'processed')) return 'pi_todo';
              return 'hidden';
            };
            const counts = { all: inbShips.length, pi_todo: 0, no_pi: 0, done: 0 };
            inbShips.forEach(sh => { const k = piState(sh); if (counts[k] != null) counts[k]++; });
            const piTotal = inbShips.reduce((t, sh) => t + sh.pis.length, 0);
            const list = inbShips
              .filter(sh => inbFilter === 'all' || piState(sh) === inbFilter)
              .sort((a, b) => String(b.when).localeCompare(String(a.when)) || String(b.code).localeCompare(String(a.code)));
            // ลำดับ = นับรายปีตามวันของถึง: 01 = shipment แรกของปี เลขมากสุด = ล่าสุด · ไม่นับใบที่ยกเลิก (void)
            // นับจากรายการเต็มเสมอ เลขจะไม่ขยับตามตัวกรอง
            const seqById = new Map();
            const perYear = new Map();
            [...inbShips].filter(sh => sh.status !== 'void')
              .sort((a, b) => String(a.when).localeCompare(String(b.when)) || String(a.code).localeCompare(String(b.code)))
              .forEach(sh => {
                const y = String(sh.when).slice(0, 4);
                const n = (perYear.get(y) ?? 0) + 1;
                perYear.set(y, n);
                seqById.set(sh.id, n);
              });
            const fmtD = d => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00+07:00`).toLocaleDateString('th-TH-u-ca-gregory', { timeZone: TZ, day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');
            const toggle = (id, what) => setInbOpen(o => ({ ...o, [id]: o[id] === what ? null : what }));
            const chips = [['all', 'ทั้งหมด'], ['pi_todo', 'มี PI รอ Process'], ['no_pi', 'ยังไม่มี PI'], ['done', 'ทำใบสั่งซื้อแล้ว']];
            return (
              <div className="drafts-section">
                <div className="inb-head">
                  <div className="eyebrow" style={{ margin: 0 }}>
                    📥 Shipment จากระบบนำเข้า · Inbound ({inbShips.length}) · มีไฟล์ PI {piTotal}
                  </div>
                  <div className="inb-head-r">
                    {inbAt && <span className="inb-at">ข้อมูล ณ {inbAt.toLocaleTimeString('th-TH', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}</span>}
                    <button className="ghost-btn" onClick={syncInbound} disabled={inbSync}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M20 12a8 8 0 11-2.3-5.6M20 4v4h-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                      {inbSync ? 'กำลังดึง…' : 'ดึงข้อมูลใหม่'}
                    </button>
                  </div>
                </div>
                <p className="dz-note" style={{ margin: '0 0 8px' }}>
                  <span>ทุก shipment ในแอป Inbound เรียงตามวันของถึงล่าสุดก่อน — อันที่มีไฟล์ PI กด Process แล้ว AI จะอ่าน PDF สร้างเป็นร่างให้ตรวจ/ใส่เรท/ยืนยันตามปกติ</span>
                </p>
                <div className="inbs-chips">
                  {chips.map(([k, l]) => (
                    <button key={k} className={`inbs-chip${inbFilter === k ? ' on' : ''}`} onClick={() => setInbFilter(k)}>
                      {l} <b>{counts[k]}</b>
                    </button>
                  ))}
                  {inbShips.some(sh => sh.pis.some(p => p.link?.status === 'hidden')) && (
                    <button className="inbs-chip" onClick={() => setInbShowHidden(v => !v)}>
                      {inbShowHidden ? 'ซ่อนไฟล์ที่พับไว้' : 'แสดงไฟล์ PI ที่ซ่อน'}
                    </button>
                  )}
                </div>
                {list.length === 0 && (
                  <p className="sd-empty" style={{ margin: '4px 0 0' }}>
                    {inbShips.length === 0 ? 'ยังไม่มี shipment ในแอป Inbound' : 'ไม่มี shipment ตามตัวกรองนี้'}
                  </p>
                )}
                {list.length > 0 && (
                  <div className="inb-hd inbs-grid">
                    <span>#</span><span>ของถึง</span><span>แบรนด์</span><span>Shipment</span><span>สถานะ</span>
                  </div>
                )}
                {list.map(sh => {
                  const st = INB_STATUS[sh.status] ?? [sh.status, ''];
                  const pis = sh.pis.filter(p => inbShowHidden || p.link?.status !== 'hidden');
                  const hiddenPis = sh.pis.length - pis.length;
                  const open = inbOpen[sh.id];
                  const supplierTxt = sh.con?.supplier_name?.trim();
                  return (
                    <div className={`inbs-row${sh.status === 'void' ? ' void' : ''}`} key={sh.id}>
                      <div className="inbs-grid">
                        <div className="inb-n">{sh.status === 'void' ? '—' : String(seqById.get(sh.id) ?? 0).padStart(2, '0')}</div>
                        <div className="inb-date mono" title={sh.eta ? 'วันของถึง (ETA)' : sh.etd ? 'วันออกเดินทาง (ETD) — ยังไม่ใส่ ETA' : 'วันที่สร้างใน Inbound — ยังไม่ใส่ ETA/ETD'}>
                          {fmtD(sh.when)}{!sh.eta && <span className="inbs-dim">{sh.etd ? ' ETD' : ' สร้าง'}</span>}
                        </div>
                        <div className="inb-brand">{inbBrandOf({ ship: sh, con: sh.con })}</div>
                        <div className="inb-main">
                          <div className="inb-name">{sh.label?.trim() || sh.code}</div>
                          <div className="inb-sub">
                            {[sh.code, sh.mode === 'air' ? 'ทางอากาศ' : sh.mode === 'sea' ? 'ทางเรือ' : null,
                              supplierTxt, sh.con?.currency?.trim(),
                              sh.con?.invoice_no?.trim() && `เลขที่ ${sh.con.invoice_no.trim()}`,
                            ].filter(Boolean).join(' · ')}
                          </div>
                          <div className="inbs-more">
                            <a onClick={() => sh.docs.length && toggle(sh.id, 'docs')} className={sh.docs.length ? '' : 'off'}>
                              เอกสาร {sh.docs.length} ไฟล์{sh.docs.length ? (open === 'docs' ? ' ▴' : ' ▾') : ''}
                            </a>
                            <a onClick={() => sh.items.length && toggle(sh.id, 'items')} className={sh.items.length ? '' : 'off'}>
                              สินค้า {sh.items.length} รายการ{sh.items.length ? (open === 'items' ? ' ▴' : ' ▾') : ''}
                            </a>
                            {sh.hasSlip && <span className="inb-badge ok">มีสลิปโอน ✓</span>}
                          </div>
                        </div>
                        <div className="inbs-status"><span className={`inbs-st ${st[1]}`}>{st[0]}</span></div>
                      </div>

                      {/* ไฟล์ PI ของ shipment นี้ — ปุ่มเหมือนเดิมทุกอย่าง */}
                      {pis.map(r => {
                        const done = r.link?.status === 'processed';
                        const hidden = r.link?.status === 'hidden';
                        return (
                          <div className="inbs-pi" key={r.id} style={hidden ? { opacity: .45 } : undefined}>
                            <div className="inbs-pi-name">
                              <span className="inbs-pi-tag">PI</span>{r.file_name || r.file_path}
                              <span className="inbs-dim"> · อัปโหลด {fmtD(thDay(r.uploaded_at))}</span>
                            </div>
                            <div className="inb-act">
                              {done && <span className="inb-badge ok">✓ ทำแล้ว{r.link?.po_no ? ` · ${r.link.po_no}` : ''}</span>}
                              <button className="ghost-btn" onClick={() => inbView(r)}>ดูไฟล์</button>
                              <button className="ghost-btn" onClick={() => inbDownload(r)} title="ดาวน์โหลดไฟล์ต้นฉบับ (นามสกุลเดิม)">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 4v11M12 15l-4-4M12 15l4-4M5 19h14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                                ดาวน์โหลด
                              </button>
                              {!done && (
                                <button className="ghost-btn" onClick={() => inbProcess(r)} disabled={!!inbBusy}>
                                  {inbBusy === r.id ? 'AI กำลังอ่าน…' : 'Process →'}
                                </button>
                              )}
                              {!done && (
                                <button className="ghost-btn" onClick={() => inbHide(r, !hidden)}>{hidden ? 'เลิกซ่อน' : 'ซ่อน'}</button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {!sh.pis.length && <div className="inbs-nopi">ยังไม่มีไฟล์ PI ใน Inbound — ดูเอกสารอื่นได้จาก "เอกสาร" ด้านบน</div>}
                      {sh.pis.length > 0 && !pis.length && hiddenPis > 0 && (
                        <div className="inbs-nopi">ไฟล์ PI ถูกซ่อนไว้ {hiddenPis} ไฟล์ — กด "แสดงไฟล์ PI ที่ซ่อน"</div>
                      )}

                      {open === 'docs' && (
                        <div className="inbs-list">
                          {sh.docs.map(d => (
                            <div className="inbs-li" key={d.id}>
                              <span className="inbs-dtype">{INB_DOC_TYPE[d.req_type] ?? d.req_type}</span>
                              <span className="inbs-dname">{d.file_name || d.file_path}</span>
                              <span className="inbs-dim mono">{fmtD(thDay(d.uploaded_at))}</span>
                              <span className="inbs-dact">
                                <a onClick={() => inbView(d)}>ดู</a>
                                <a onClick={() => inbDownload(d)}>ดาวน์โหลด</a>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {open === 'items' && (
                        <div className="inbs-list">
                          {sh.items.map(li => (
                            <div className="inbs-li items" key={li.id}>
                              <span className="inbs-dname">{li.description || '—'}</span>
                              <span className="inbs-dim mono">{li.sku || ''}</span>
                              <span className="mono inbs-qty">{li.qty != null ? Number(li.qty).toLocaleString('en-US') : '—'}{li.unit ? ` ${li.unit}` : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {inbMsg && <p className={inbMsg.startsWith('✓') ? 'saved-note' : 'err-note'} style={{ marginTop: 10 }}>{inbMsg}</p>}
              </div>
            );
          })()}
        </section>
      )}

      {/* ============ STEP 1.5 : COLUMN MAPPING ============ */}
      {step === 1.5 && parsed && (
        <section>
          <div className="back-row">
            <a onClick={() => setStep(1)}>← กลับไปแก้ไฟล์ / FX / เลขที่</a>
          </div>
          <div className="hero" style={{ paddingBottom: 10 }}>
            <div className="eyebrow">จับคู่คอลัมน์ · Map columns</div>
            <h1 style={{ fontSize: 44 }}>คอลัมน์ไหน<br />คือข้อมูลอะไร</h1>
            <p className="sub">ครั้งแรกของ layout นี้ต้องจับคู่เอง — ระบบจะจำไว้ ครั้งหน้าอ่านอัตโนมัติทันที
              (บังคับ: รหัสซัพพลายเออร์ + จำนวน)</p>
          </div>
          <div className="map-grid">
            {parsed.headers.map(h => (
              <div className="map-cell" key={h}>
                <div className="map-pi mono">{h}</div>
                <select className="under" value={mapping[h] ?? ''}
                        onChange={e => {
                          const next = { ...mapping };
                          // a system field can be used once — clear it elsewhere
                          for (const k of Object.keys(next)) if (next[k] === e.target.value) delete next[k];
                          if (e.target.value) next[h] = e.target.value; else delete next[h];
                          setMapping(next);
                        }}>
                  <option value="">— ไม่ใช้ —</option>
                  {SYSTEM_FIELDS.map(([f, th]) => <option key={f} value={f}>{th}</option>)}
                </select>
              </div>
            ))}
          </div>
          {!isComplete(mapping) && (
            <div className="cost-note" style={{ marginTop: 22 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flex: 'none', marginTop: 1 }}><path d="M12 3l9 16H3z" stroke="#BF7A15" strokeWidth="1.4"/><path d="M12 10v4M12 16h.01" stroke="#BF7A15" strokeWidth="1.6"/></svg>
              <span>ยังไม่ครบ — ต้องจับคู่ "รหัสซัพพลายเออร์" และ "จำนวน" ก่อน</span>
            </div>
          )}
          <p className="err-note">{err}</p>
          <div className="cta-row">
            <button className="arrow" disabled={!isComplete(mapping) || busy} onClick={() => toReview()} aria-label="อ่านใบ PI">
              <ArrowSvg />
            </button>
            <span className="txt">อ่านใบ PI ({parsed.rows.length} แถว)</span>
          </div>
        </section>
      )}

      {/* ============ STEP 2 : REVIEW ============ */}
      {step === 2 && (
        <section>
          <div className="back-row">
            <a onClick={() => setStep(1)}>← กลับไปแก้ไฟล์ / FX / เลขที่</a>
            {parsed && <a onClick={() => setStep(1.5)}>แก้การจับคู่คอลัมน์</a>}
            <a onClick={syncFromPd} title="แก้สินค้าใน PD แล้ว (เปลี่ยนรหัส/ชื่อ/ลบตัวซ้ำ) กดตรงนี้ให้ร่างนี้ตรงกับ PD ล่าสุด + ลองจับคู่บรรทัดที่ยังไม่เจอใหม่">
              {syncBusy ? 'กำลังเทียบกับ PD…' : '↻ ดึงข้อมูลสินค้าล่าสุดจาก PD'}
            </a>
          </div>
          {syncMsg && <div className={`sync-note${syncMsg.warn ? ' warn' : ''}`}>{syncMsg.text}</div>}
          {dupGroups.length > 0 && (
            <div className="dup-note">
              <b>⚠ สินค้าเดียวกันถูกจับคู่มากกว่า 1 บรรทัด — ตรวจก่อนยืนยัน</b>
              {dupGroups.map(g => (
                <div key={g.sku}>{g.sku} · {g.name} ← รหัสบนใบ {g.codes.join(', ')}</div>
              ))}
            </div>
          )}
          <div className="review-head">
            <div className="supplier-block">
              <div className="eyebrow">กำลังตรวจสอบ · {reference || 'ไม่มีเลขที่ PI'}</div>
              <div className="supplier-name">{supplier?.company_name}</div>
              <div className="supplier-meta">
                {supplier?.default_brand?.name && <span className="tag solid">{supplier.default_brand.name}</span>}
                <span className="tag">{currency}</span>
                {currency?.toUpperCase() !== 'THB' && (
                  <label className={`fx-inline${fxNum ? '' : ' missing'}`} title="เรทนี้ใช้คิดต้นทุนเป็นบาท (ส่งเข้า PD + รับของ) — แก้ได้ตรงนี้">
                    FX <input className="mono" inputMode="decimal" value={fx} onChange={e => onFxInput(e.target.value.replace(/[^0-9.]/g, ''))}
                              placeholder="ใส่เรท" />
                    {fxAuto ? <span className="fx-auto">ค่ากลาง</span> : (fxDefault && fxNum && fxNum !== fxDefault ? <span className="fx-note">ค่ากลาง {fxDefault}</span> : null)}
                  </label>
                )}
                {fxWarn && <span className="fx-warn inline">⚠ {fxWarn}</span>}
                <span className="mono">{active.length} รายการ{active.length < lines.length ? ` (ไม่รวม ${lines.length - active.length})` : ''}</span>
              </div>
            </div>
            <div className="counts">
              <div className="count"><div className="num">{linked}</div><div className="lab">เชื่อมแล้ว<span className="en">Linked</span></div></div>
              <div className={`count ${needed ? 'alert' : ''}`}><div className="num">{needed}</div><div className="lab">ต้องสร้าง<span className="en">New — action</span></div></div>
            </div>
          </div>

          {/* ของมาเป็นกล่อง: ทั้งใบมักขนาดเดียวกัน (Nalgene = 6 ทุกรุ่น) ตั้งทีเดียวทุกบรรทัดสินค้า */}
          <div className="pack-bar">
            <span className="pb-q">ของเจ้านี้มาเป็นกล่องไหม?</span>
            <span className="pb-hint">ถ้ามาเป็นกล่อง ใส่จำนวนชิ้นต่อกล่อง — ระบบจะบันทึกเป็นจำนวนชิ้นกับราคาต่อชิ้นให้เอง</span>
            <div className="pb-actions">
              <button className="ghost-btn small" onClick={() => setAllPacks(6)}>ตั้งทั้งใบ = 6 ชิ้น/กล่อง</button>
              <button className="ghost-btn small" onClick={() => setAllPacks(1)}>ไม่ได้มาเป็นกล่อง (ตั้ง 1)</button>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style={{ width: 38 }} />
                <th style={{ width: 165 }}><span className="th">ประเภทรายการ</span><span className="en">Line type</span></th>
                <th style={{ width: 170 }}><span className="th">รหัสซัพพลายเออร์</span><span className="en">Supplier code</span></th>
                <th><span className="th">รายละเอียด</span><span className="en">Description</span></th>
                <th className="r" style={{ width: 86 }}><span className="th">จำนวน</span><span className="en">ตามใบ PI</span></th>
                <th className="r" style={{ width: 92 }} title="ของบางเจ้ามาเป็นกล่อง เช่น Nalgene 1 กล่อง = 6 ขวด — ใส่ 6 แล้วระบบจะบันทึกเป็นจำนวนขวดกับราคาต่อขวดให้เอง (ไม่มากับกล่องใส่ 1)"><span className="th">ต่อกล่อง</span><span className="en">Pack</span></th>
                <th className="r" style={{ width: 120 }} title="ทุนที่เราจ่ายซัพพลายเออร์ตามใบ PI (ถ้ามาเป็นกล่อง = ราคาต่อกล่อง) — ไม่ใช่ราคาขายปลีก"><span className="th">ราคาซื้อ {sym}</span><span className="en">ตามใบ PI</span></th>
                <th className="r" style={{ width: 130 }}><span className="th">รวม {sym}</span><span className="en">Amount</span></th>
                <th style={{ width: 300 }}><span className="th">สินค้าในระบบ</span><span className="en">Catalog link</span></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.code}-${i}`} className={`${l.excluded ? 'excluded' : ''} ${!l.excluded && l.lineType === 'product' && !l.product ? 'needs' : ''} ${dupIdx.has(i) ? 'dup' : ''}`}>
                  <td>
                    <input type="checkbox" checked={!l.excluded} onChange={() => toggleExcluded(i)}
                      title={l.excluded ? 'รวมกลับเข้ารายการ' : 'เอาออกจากรายการ'} />
                  </td>
                  <td>
                    <select className={`type-select ${l.lineType}`} value={lineCat(l)}
                            onChange={e => setLineCategory(i, e.target.value)}
                            title="รายการนี้คืออะไร — สินค้าเข้าสต๊อก ที่เหลือเข้า PO เป็นค่าใช้จ่าย">
                      {LINE_CATEGORIES.map(c => (
                        <option key={c.key} value={c.key}>{c.th}{c.zort && c.key !== 'product' ? ` — Zort: ${c.zort}` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td><span className="code">{l.code}</span></td>
                  <td>
                    <div className="desc-cell">
                      {l.product?.image_url
                        ? <div className="thumb"><img src={l.product.image_url} alt="" /></div>
                        : <div className="thumb empty"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="14" rx="1.5" stroke="#CBCBCB" strokeWidth="1.4"/><circle cx="9" cy="10" r="1.5" stroke="#CBCBCB" strokeWidth="1.4"/><path d="M5 17l4-4 3 3 3-3 4 4" stroke="#CBCBCB" strokeWidth="1.4"/></svg></div>}
                      <span className="desc">{l.desc || '—'}</span>
                    </div>
                  </td>
                  <td className="r">
                    <input className="cell-num" inputMode="decimal" value={l.qty ?? ''}
                           onChange={e => setLineNum(i, 'qty', e.target.value)} />
                    {packOf(l) > 1 && <div className="pack-calc">= {fmt(realQty(l))} ชิ้น</div>}
                  </td>
                  <td className="r">
                    {l.lineType === 'product' ? (
                      <>
                        <input className="cell-num" inputMode="numeric" value={l.pack ?? ''} placeholder="1"
                               onChange={e => setLineNum(i, 'pack', e.target.value)} />
                        {l._packFrom === 'remembered' && packOf(l) > 1 &&
                          <div className="pack-calc">จำจากครั้งก่อน</div>}
                      </>
                    ) : <span className="num" style={{ color: 'var(--g3)' }}>—</span>}
                  </td>
                  <td className="r">
                    <input className="cell-num" inputMode="decimal" value={l.unit ?? ''}
                           onChange={e => setLineNum(i, 'unit', e.target.value)} />
                    {packOf(l) > 1 && <div className="pack-calc">= {sym}{fmt(realUnit(l), 2)}/ชิ้น</div>}
                    {l._costFrom === 'pd' && <div className="fee-note" style={{ whiteSpace: 'nowrap' }}>จากซื้อล่าสุด PD</div>}
                  </td>
                  <td className="r"><span className="num">{fmt(l.amount ?? (Number(l.qty) || 0) * (Number(l.unit) || 0))}</span></td>
                  <td>
                    {l.lineType === 'fee' ? (
                      <span className="fee-note">ไม่ต้องเชื่อมสินค้า — เข้า PO เป็น{catTh(lineCat(l))}</span>
                    ) : l.product ? (
                      // รูป+ชื่อ+ราคาขายดึงสดจาก PD (แม่ข้อมูล) — สำเนา BUY ใช้แค่ผูก id
                      <div className="link-cell linked">
                        {(pdInfo[l.product.sku]?.image_url || l.product.image_url)
                          ? <div className="thumb"><img src={pdInfo[l.product.sku]?.image_url || l.product.image_url} alt="" /></div>
                          : <div className="thumb empty"><svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="14" rx="1.5" stroke="#CBCBCB" strokeWidth="1.4"/><circle cx="9" cy="10" r="1.5" stroke="#CBCBCB" strokeWidth="1.4"/><path d="M5 17l4-4 3 3 3-3 4 4" stroke="#CBCBCB" strokeWidth="1.4"/></svg></div>}
                        <div>
                          <div className="prod">{pdInfo[l.product.sku]?.name_en || pdInfo[l.product.sku]?.name_th || l.product.name}</div>
                          <div className="sku">{l.product.sku ?? '—'}
                            {pdInfo[l.product.sku]?.purchase_cost != null ? ` · ซื้อ ฿${fmt(pdInfo[l.product.sku].purchase_cost)}` : ''}
                            {pdInfo[l.product.sku]?.default_price != null ? ` · ขาย ฿${fmt(pdInfo[l.product.sku].default_price)}` : ''}
                            {l.source === 'remembered' ? ' · จำจากครั้งก่อน' : l.source === 'created' ? ' · สร้างใหม่' : l.source === 'manual' ? ' · จับคู่เอง' : ''}</div>
                          {l.source === 'renamed' && <div className="link-flag">รหัสบนใบ {l.code} = SKU ใหม่ใน PD</div>}
                          {l._renamedFrom && <div className="link-flag">PD เปลี่ยนรหัสจาก {l._renamedFrom}</div>}
                          {l._inactive && <div className="link-flag warn">สินค้านี้ถูกปิดใช้งานใน PD — ตรวจว่าจับคู่ถูกตัว</div>}
                          {dupIdx.has(i) && <div className="link-flag warn">ตัวเดียวกับบรรทัดอื่น — ตรวจสี/ขนาด</div>}
                          <button className="mini-edit" onClick={() => setPickIdx(i)}>เปลี่ยน</button>
                        </div>
                      </div>
                    ) : (
                      <div className="link-actions">
                        {l._dropped && <div className="link-flag warn">สินค้าที่เคยจับคู่ ({l._dropped}) ถูกลบออกจาก PD แล้ว</div>}
                        <button className="btn-create" onClick={() => guardCreate(i)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7"/></svg>
                          สร้างสินค้า
                        </button>
                        <button className="mini-edit" onClick={() => setPickIdx(i)}
                                title="ค้นหาสินค้าที่มีอยู่แล้วในระบบมาผูกกับบรรทัดนี้ — ใช้เมื่อรหัสในใบไม่ตรงกับ SKU เป๊ะ">
                          🔗 จับคู่สินค้าเดิม
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="review-foot">
            <div className="totals">
              <div className="tot"><div className="lab">มูลค่าใบ PI<span className="en">Invoice total</span></div><div className="val mono">{sym}{fmt(totForeign)}</div></div>
              <div className="tot"><div className="lab">≈ บาท (ประมาณ)<span className="en">≈ THB provisional</span></div><div className="val small mono">฿{fmt(totForeign * fxNum)}</div></div>
            </div>
            <div className="review-ctas">
              <button className="ghost-btn" onClick={saveDraft} disabled={busy}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="1.5"/><path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="1.5"/></svg>
                {draftId ? 'อัปเดตร่าง' : 'บันทึกร่าง'}
              </button>
              <div className={`complete-cta ${needed ? 'locked' : ''}`}>
                <button className="arrow" disabled={needed > 0 || busy} onClick={complete} aria-label="ยืนยันใบสั่งซื้อ">
                  <ArrowSvg />
                </button>
                <span className="txt">{needed ? `เหลืออีก ${needed} รายการต้องสร้าง` : 'ยืนยันใบสั่งซื้อ'}</span>
              </div>
            </div>
          </div>
          {savedMsg && <p className="saved-note">{savedMsg}</p>}
          <p className="err-note">{err}</p>
        </section>
      )}

      {/* ============ STEP 3 : COMPLETE ============ */}
      {step === 3 && done && (
        <section>
          <div className="done-wrap">
            <div className="done-check">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="#0A0A0A" strokeWidth="1.7"/></svg>
            </div>
            <div className="eyebrow">ยืนยันใบสั่งซื้อแล้ว · PO confirmed</div>
            <h1 className="done">ของกำลังมา</h1>
            <p className="done-sub">
              ใบสั่งซื้อ <span className="mono">{done.poNo}</span>{reference ? <> (PI <span className="mono">{reference}</span>)</> : null} ล็อกเรียบร้อย
              ตอนนี้ StockPoint แสดงทั้ง {done.skuCount} SKU เป็น <strong>กำลังเข้า</strong> —
              พนักงานหน้าร้านค้นเจอแม้สต๊อกเป็นศูนย์ และขายล่วงหน้าให้ลูกค้าได้เลย
            </p>
            <div className="handoff">
              <div className="row"><span className="k">ส่งเข้า StockPoint</span><span className="v amber">{done.skuCount} SKU · กำลังเข้า</span></div>
              <div className="row"><span className="k">สร้างสินค้าใหม่</span><span className="v">{done.newCount} รายการ</span></div>
              {done.feeCount > 0 && <div className="row"><span className="k">ค่าใช้จ่าย / ค่าบริการ</span><span className="v">{done.feeCount} รายการ</span></div>}
              <div className="row"><span className="k">ส่งเข้า PD</span>
                {done.pdPush?.ok
                  ? <span className="v">บันทึกแล้ว · รอกดรับ/เติมราคาในหน้า "จัดซื้อ" ของ PD</span>
                  : <span className="v" style={{ color: 'var(--accent)' }}>{done.pdPush?.error || 'ยังไม่ได้ส่ง'} — ส่งซ้ำได้จากหน้าใบสั่งซื้อ</span>}
              </div>
              <div className="row"><span className="k">ต้นทุน (landed cost)</span><span className="v">เก็บในฝ่ายจัดซื้อ · ซ่อนจาก StockPoint</span></div>
              <div className="row"><span className="k">ส่งบัญชี</span><span className="v" style={{ color: 'var(--g2)' }}>Phase 6 — รอยืนยันรูปแบบ</span></div>
            </div>
            <div className="done-actions">
              <button className="ghost-btn fill" onClick={resetAll}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="currentColor" strokeWidth="1.6"/></svg>
                นำเข้าใหม่
              </button>
              <button className="ghost-btn" onClick={onGoOrders}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M5 7h14M5 12h14M5 17h9" stroke="currentColor" strokeWidth="1.6"/></svg>
                ดูใบสั่งซื้อทั้งหมด
              </button>
            </div>
          </div>
        </section>
      )}

      {createIdx !== null && (
        <ProductDrawer
          line={lines[createIdx]}
          initialBarcode={lines[createIdx]?.barcode ?? ''}
          supplier={supplier}
          currency={currency}
          onClose={() => setCreateIdx(null)}
          onSaved={product => {
            setLines(ls => ls.map((l, i) => i === createIdx ? { ...l, product, source: 'created' } : l));
            newCount.current += 1;
            setCreateIdx(null);
          }}
        />
      )}

      {/* จับคู่สินค้าที่มีอยู่แล้ว — รหัสในใบ (เช่นรหัสประกอบจาก by Photos) ไม่ตรง SKU เป๊ะ ระบบ match เองไม่ได้
          picker ค้น PD-first + prefill รหัสจากบรรทัด (หาเต็มไม่เจอจะลองรหัสต้นให้เอง) · จับคู่แล้วระบบจำ
          รหัสซัพพลายเออร์→สินค้า ให้ตอนยืนยันใบ (supplier_products) รอบหน้า match อัตโนมัติ */}
      {pickIdx !== null && (
        <ProductPicker
          initialQuery={pickQuery ?? lines[pickIdx]?.code ?? ''}
          onClose={() => { setPickIdx(null); setPickQuery(null); }}
          onPick={p => {
            // บรรทัดจาก by Photos มี barcode ที่ AI อ่านจากป้าย — จับคู่แล้วเติมกลับเข้า PD ด้วย
            backfillBarcode(p.sku, p.name, lines[pickIdx]?.barcode);
            setLines(ls => ls.map((l, i) => i === pickIdx
              ? { ...l, product: { id: p.id, name: p.name, sku: p.sku, image_url: p.image_url ?? null }, source: 'manual' }
              : l));
            setPickIdx(null); setPickQuery(null);
          }}
        />
      )}
    </>
  );
}
